import { APP_CONFIG } from '../constants/constants';

/**
 * Check if the value is a valid email.
 * 
 * @param val email to check.
 * @returns boolean
 */
export const isEmail = (val: string) => {
    return val.match(/^[\w-]+(\.[\w-]+)*@[\w-]+(\.[\w-]+)+$/)
}

/**
 * Check if password is secure enough.
 * 
 * The password must have:
 * - At least 8 characters (minimum secure length)
 * - At least 1 upper case and 1 lower case character
 * - At least 1 number
 * - At least 1 special character
 * - Maximum 128 characters to prevent DoS attacks
 */
export const ifPasswordIsSecure = (val: string) => {
    // Use constants for consistent configuration
    const MIN_LENGTH = APP_CONFIG.MIN_PASSWORD_LENGTH;
    const MAX_LENGTH = APP_CONFIG.MAX_PASSWORD_LENGTH;
    
    // Check length constraints first
    if (val.length < MIN_LENGTH || val.length > MAX_LENGTH) {
        return null;
    }
    
    // Enhanced regex with proper length constraints
    return val.match(new RegExp(`^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&,.\\-;:_])[A-Za-z\\d@$!%*?&,.\\-;:_]{${MIN_LENGTH},${MAX_LENGTH}}$`));
}

/**
 * Get detailed password validation feedback using i18n translations
 * @param val password string to validate
 * @param t translation function from useTranslation hook
 * @returns array of translated error messages
 */
export const getPasswordValidationErrors = (val: string, t: (key: string, options?: any) => string): string[] => {
    const errors: string[] = [];
    const MIN_LENGTH = APP_CONFIG.MIN_PASSWORD_LENGTH;
    const MAX_LENGTH = APP_CONFIG.MAX_PASSWORD_LENGTH;
    
    if (val.length < MIN_LENGTH) {
        errors.push(t('passwordTooShort', { minLength: MIN_LENGTH }));
    }
    
    if (val.length > MAX_LENGTH) {
        errors.push(t('passwordTooLong', { maxLength: MAX_LENGTH }));
    }
    
    if (!/[a-z]/.test(val)) {
        errors.push(t('passwordMissingLowercase'));
    }
    
    if (!/[A-Z]/.test(val)) {
        errors.push(t('passwordMissingUppercase'));
    }
    
    if (!/\d/.test(val)) {
        errors.push(t('passwordMissingNumber'));
    }
    
    if (!/[@$!%*?&,.\-;:_]/.test(val)) {
        errors.push(t('passwordMissingSpecialChar'));
    }
    
    return errors;
}